import { useState } from "react";
import { Link as RouterLink } from "react-router";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { Search } from "lucide-react";
import dayjs from "dayjs";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import Badge from "../../components/ui/badge/Badge";
import type { OnboardingListRow } from "../../types/onboarding";
import { PLAIN_BUTTONS } from "./buttons";
import { toDaysAgo } from "./checklistRows";
import { STAGES, partyLabel } from "./labels";
import ExistingCompanyDialog from "./ExistingCompanyDialog";
import { useOnboardingDashboard } from "./useOnboardingDashboard";

type View = "open" | "waiting" | "overdue" | "completed";

// Step titles name the finished state; a waiting step is not there yet.
const toPendingTitle = (title: string) => title.replace(/ (live|complete)( for new MIDs)?$/, "$2");

// The count cards are the filter; a client counts under every card that applies to it.
const OPEN_CARDS: { key: View; label: string; match: (row: OnboardingListRow) => boolean }[] = [
  { key: "open", label: "In onboarding", match: () => true },
  { key: "waiting", label: "Waiting", match: (r) => r.waiting > 0 },
  { key: "overdue", label: "Overdue", match: (r) => r.overdue },
];

function CountCard({ label, count, pressed, onClick, tone }: {
  label: string;
  count: number | string | undefined;
  pressed: boolean;
  onClick: () => void;
  tone?: "alert" | "muted";
}) {
  return (
    <button
      type="button" aria-pressed={pressed} onClick={onClick}
      className={`flex cursor-pointer flex-col gap-0.5 rounded-[10px] text-left ${
        pressed ? "border-2 border-gray-800 px-[13px] py-[11px] dark:border-white/90" : "border border-gray-200 px-3.5 py-3 dark:border-gray-800"
      } ${tone === "muted" && !pressed ? "bg-gray-50 dark:bg-white/[0.03]" : ""}`}
    >
      <span className={`text-xs font-medium ${pressed ? "text-gray-700 dark:text-gray-300" : "text-gray-500"}`}>{label}</span>
      <span className={`text-2xl font-semibold ${tone === "alert" ? "text-error-600" : tone === "muted" && !pressed ? "text-gray-500" : "text-gray-800 dark:text-white/90"}`}>
        {count ?? "…"}
      </span>
    </button>
  );
}

export default function OnboardingDashboard() {
  const [view, setView] = useState<View>("open");
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);
  const { open, counts, completed } = useOnboardingDashboard(view === "completed");
  const matchesSearch = (r: { companyName: string }) => r.companyName.toLowerCase().includes(search.trim().toLowerCase());
  const openRows = open.data ?? [];
  const card = OPEN_CARDS.find((c) => c.key === view);
  const rows = card ? openRows.filter((r) => card.match(r) && matchesSearch(r)) : [];
  const completedRows = (completed.data ?? []).filter(matchesSearch);
  const list = view === "completed" ? completed : open;
  const listed = view === "completed" ? completedRows.length : rows.length;

  return (
    <Box sx={PLAIN_BUTTONS}>
      <PageMeta title="Chargemont | Onboarding" description="Clients in onboarding" />
      <PageContainer>
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-white/90">Onboarding</h1>
            <div className="flex gap-2">
              <Button variant="outlined" onClick={() => setPicking(true)}>Existing company</Button>
              <Button variant="contained" component={RouterLink} to="/onboarding/new">New client</Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {OPEN_CARDS.map((c) => (
              <CountCard
                key={c.key} label={c.label} count={open.error ? "—" : open.data ? openRows.filter(c.match).length : undefined}
                pressed={view === c.key} onClick={() => setView(c.key)} tone={c.key === "overdue" ? "alert" : undefined}
              />
            ))}
            <CountCard label="Completed" count={counts.error ? "—" : counts.data?.completed} pressed={view === "completed"} onClick={() => setView("completed")} tone="muted" />
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
            <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-2.5 dark:bg-white/[0.03]">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{list.data && (listed === 1 ? "1 client" : `${listed} clients`)}</span>
              <div className="relative w-60 min-w-0">
                <Search size={15} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-gray-400" />
                <input
                  value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients" aria-label="Search clients"
                  className="h-8 w-full rounded-lg border border-gray-200 bg-white pr-3 pl-[33px] text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-brand-500 focus:ring-3 focus:ring-brand-500/15 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                />
              </div>
            </div>
            {view === "completed" && completed.data && (
              <>
                <div className="hidden grid-cols-[minmax(0,1.6fr)_110px_100px_minmax(0,1.6fr)_80px] gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-medium text-gray-500 lg:grid dark:border-gray-800 dark:bg-white/5">
                  <span>Client</span><span>Finished</span><span>Took</span><span>Last step</span><span className="text-right">Steps</span>
                </div>
                {completedRows.map((r) => (
                  <RouterLink
                    key={r.onboardingId} to={`/onboarding/${r.onboardingId}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-t border-gray-100 px-4 py-3 text-sm hover:bg-gray-50 lg:grid-cols-[minmax(0,1.6fr)_110px_100px_minmax(0,1.6fr)_80px] dark:border-gray-800 dark:hover:bg-white/5"
                  >
                    <span className="font-medium text-gray-800 dark:text-white/90">{r.companyName}</span>
                    <span className="text-gray-500 max-lg:justify-self-end">{dayjs(r.finishedOn).format("MMM D")}</span>
                    <span className="text-gray-500">{r.daysTaken === 1 ? "1 day" : `${r.daysTaken} days`}</span>
                    <span className="text-gray-500 max-lg:col-span-2">{r.lastStep ?? "—"}</span>
                    <span className="text-success-600 lg:text-right">{r.done} / {r.total}</span>
                  </RouterLink>
                ))}
                {!completedRows.length && <div className="px-4 py-8 text-center text-sm text-gray-500">{search.trim() ? "No clients match" : "No finished onboardings yet"}</div>}
              </>
            )}
            {view !== "completed" && open.data && (
              <>
                <div className="hidden grid-cols-[minmax(0,1.3fr)_110px_70px_110px_150px_minmax(0,2fr)] gap-3 border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-medium text-gray-500 lg:grid dark:border-gray-800 dark:bg-white/5">
                  <span>Client</span><span>Signed</span><span>Brands</span><span>Stage</span><span>Progress</span><span>Waiting</span>
                </div>
                {rows.map((r) => (
                  <RouterLink
                    key={r.onboardingId} to={`/onboarding/${r.onboardingId}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 border-t border-gray-100 px-4 py-3 text-sm hover:bg-gray-50 lg:grid-cols-[minmax(0,1.3fr)_110px_70px_110px_150px_minmax(0,2fr)] dark:border-gray-800 dark:hover:bg-white/5"
                  >
                    <span className="font-medium text-gray-800 dark:text-white/90">{r.companyName}</span>
                    <span className="text-gray-500 max-lg:hidden">{r.signedDays !== null ? toDaysAgo(r.signedDays) : `started ${toDaysAgo(r.onboardingDays)}`}</span>
                    <span className="text-gray-500 max-lg:hidden">{r.brandCount}</span>
                    <span className="justify-self-end lg:justify-self-start"><Badge size="sm" color={STAGES[r.stage].color}>{STAGES[r.stage].label}</Badge></span>
                    <span className="col-span-2 flex items-center gap-2 text-gray-500 lg:col-span-1">
                      <span className="lg:hidden">{r.signedDays !== null ? `Signed ${toDaysAgo(r.signedDays)}` : `Started ${toDaysAgo(r.onboardingDays)}`} · {r.brandCount} {r.brandCount === 1 ? "brand" : "brands"} ·</span>
                      {r.done} of {r.total}<span className="-ml-2 lg:hidden">&nbsp;done</span>
                      <span className="block h-1.5 w-16 overflow-hidden rounded-full bg-gray-100 max-lg:hidden dark:bg-gray-800">
                        <span className="block h-1.5 bg-success-500" style={{ width: `${r.total ? Math.round((r.done / r.total) * 100) : 100}%` }} />
                      </span>
                    </span>
                    <span className="col-span-2 lg:col-span-1">
                      {r.oldestWaiting
                        ? (
                          <span className={r.overdue ? "text-error-600" : "text-warning-600"}>
                            {r.waiting} waiting · <span className="text-gray-700 dark:text-gray-300">{toPendingTitle(r.oldestWaiting.title)}</span> · sent to {r.oldestWaiting.party === "network" ? partyLabel(r.oldestWaiting) : r.oldestWaiting.party} {r.oldestWaiting.days}d ago
                          </span>
                        )
                        : <span className="text-gray-400">Nothing waiting</span>}
                    </span>
                  </RouterLink>
                ))}
                {!rows.length && <div className="px-4 py-8 text-center text-sm text-gray-500">{search.trim() ? "No clients match" : "No clients in this view"}</div>}
              </>
            )}
          </div>

          {list.error && <Alert severity="error">Could not load the onboardings: {list.error}</Alert>}
          {!list.data && !list.error && <CircularProgress size={24} />}
        </div>
      </PageContainer>
      {picking && (
        <ExistingCompanyDialog openByCompany={new Map((open.data ?? []).map((r) => [r.companyId, r.onboardingId]))} onClose={() => setPicking(false)} />
      )}
    </Box>
  );
}
